/* eslint-disable max-len */
import React, {useRef, useEffect, useCallback, useState} from 'react';
import {v4 as uuidv4} from 'uuid';
import RealTimeHuman from '@bddh/starling-realtime-client';

import {checkPlayUnMute} from './utils.ts';

import './Demo.scss';

const DEFAULT_TEXT = '优一科技创立于 2015 年，是国内早期布局人工智能底层研发的科技企业。公司深耕自研算法、多模态 AI、智能硬件与大数据全域运营，依托成熟数字技术底座，形成跨行业数字化解决方案能力。前期依托医疗场景完成技术落地验证，逐步拓展多元产业数字化业务，搭建完整技术研发、项目落地、产业运营闭环，打造覆盖多行业的数智化产业服务体系。';
const OPENING_TTS_DELAY_MS = 200; // 开场白 TEXT_RENDER 延迟：等音频管线就绪后再发，避免第一个字被吞掉
const OPENING_FREE_INTERRUPT_MS = 5000; // 开场白免中断保护期上限：超过此时间强制解除静音，允许用户插话打断

// ── 后端服务地址 ──
// 开发环境默认指向本地 server（server/index.js），生产环境请改为实际后端域名
// 通过环境变量 REACT_APP_API_BASE 覆盖
const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

// ── 自有 LLM 端点（与 API_BASE 同源，OpenAI 兼容 SSE 流式）──
const LLM_CHAT_URL = `${API_BASE}/v1/chat/completions`;

/** 数字人形象配置（由后端 /api/config 下发，避免前端硬编码） */
interface DigitalHumanConfig {
    ttsPer: string;
    figureId: string;
}

/**
 * 从后端动态获取数字人访问 token
 * 后端使用 appId + appKey 生成 HMAC-SHA256 签名，避免将 appKey 暴露在前端
 *
 * @returns {Promise<string>} token 字符串，格式为 appId/signature/expireTime
 */
const fetchToken = async (): Promise<string> => {
    console.info('[Token] 开始从后端获取 token...', `${API_BASE}/api/token`);
    const res = await fetch(`${API_BASE}/api/token`);
    if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`获取 token 失败: HTTP ${res.status} ${text}`);
    }
    const json = await res.json();
    if (json.code !== 0 || !json.data?.token) {
        throw new Error(`获取 token 失败: ${json.msg || JSON.stringify(json)}`);
    }
    console.info('[Token] ✅ 获取成功，过期时间:', json.data.expireTime);
    return json.data.token as string;
};

/**
 * 从后端获取数字人形象配置（ttsPer / figureId）
 * 前端不硬编码任何形象参数，统一由后端 /api/config 下发，保持前后端分离
 *
 * @returns {Promise<DigitalHumanConfig>}
 */
const fetchConfig = async (): Promise<DigitalHumanConfig> => {
    console.info('[Config] 开始从后端获取数字人形象配置...', `${API_BASE}/api/config`);
    const res = await fetch(`${API_BASE}/api/config`);
    if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`获取数字人配置失败: HTTP ${res.status} ${text}`);
    }
    const json = await res.json();
    if (json.code !== 0 || !json.data?.ttsPer || !json.data?.figureId) {
        throw new Error(`获取数字人配置失败: ${json.msg || JSON.stringify(json)}`);
    }
    console.info('[Config] ✅ 获取成功:', json.data);
    return json.data as DigitalHumanConfig;
};

enum ReadyState {
    UNINSTANTIATED = -1,
    CONNECTING = 0,
    OPEN = 1,
    CLOSING = 2,
    CLOSED = 3,
}

const Demo = () => {
    const [subTitle, setSubTitle] = useState('');
    const [audioMode, setAudioMode] = useState('btn');
    const [pullAudioFromRTC, setPullAudioFromRtc] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const isRecordingRef = useRef(false);
    const [canTransfer, setCanTransfer] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const [isListening, setIsListening] = useState(false); // ASR 正在识别用户说话
    const canTransferRef = useRef(false);
    const hasSentGreetingRef = useRef(false);
    const enableInterruptRef = useRef(true);
    const humanInstanceRef = useRef(null);
    const isRespondingRef = useRef(false);        // 自有 LLM 回复进行中，防重入
    const abortControllerRef = useRef<AbortController | null>(null);
    const conversationHistoryRef = useRef<{role:string;content:string}[]>([]); // 对话历史

    // ── 自有 LLM 流式调用 + textStreamRender 驱动数字人 ──
    const callLlmAndDrive = useCallback(async (query: string) => {
        // 防重入：上一轮回复还没播完，先中止旧请求
        if (isRespondingRef.current) {
            abortControllerRef.current?.abort();
        }

        isRespondingRef.current = true;
        const requestId = uuidv4();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        let first = true;
        let textBuffer = '';
        let fullReply = ''; // 累积完整回复文本，用于存入对话历史

        // 将用户问题追加到对话历史
        const history = conversationHistoryRef.current;
        history.push({ role: 'user', content: query });

        // 防止历史过长：保留最近 20 条消息（10 轮对话）
        const MAX_MESSAGES = 20;
        const messages = history.length > MAX_MESSAGES
            ? history.slice(history.length - MAX_MESSAGES)
            : [...history];

        try {
            console.info('[LLM] 发送请求:', query.slice(0, 50));
            const res = await fetch(LLM_CHAT_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    stream: true,
                    messages
                }),
                signal: controller.signal
            });

            if (!res.ok) {
                throw new Error(`LLM HTTP ${res.status}`);
            }

            const reader = res.body?.getReader();
            if (!reader) throw new Error('No readable stream');

            const decoder = new TextDecoder();
            let lineBuffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                lineBuffer += decoder.decode(value, { stream: true });
                const lines = lineBuffer.split('\n');
                lineBuffer = lines.pop() || '';

                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    const data = line.slice(6).trim();
                    if (data === '[DONE]') {
                        // 发送末尾剩余文本
                        if (textBuffer) {
                            humanInstanceRef.current?.textStreamRender({
                                body: JSON.stringify({ first, last: false, text: textBuffer }),
                                requestId,
                                ...(first ? { onCallbackMsg: (res: any) => console.log('[textStreamRender]', res) } : {})
                            });
                            first = false;
                            textBuffer = '';
                        }
                        // 发送结束标志
                        humanInstanceRef.current?.textStreamRender({
                            body: JSON.stringify({ first: false, last: true, text: '' }),
                            requestId
                        });
                        console.info('[LLM] 流式响应结束');
                        continue;
                    }

                    try {
                        const json = JSON.parse(data);
                        const content: string | undefined = json.choices?.[0]?.delta?.content;
                        if (!content) continue;

                        textBuffer += content;
                        fullReply += content;

                        // 遇到标点或攒够10字，flush 一个 textStreamRender 块
                        const shouldFlush = /[。！？，、\n!?,.;:]/.test(textBuffer.slice(-1)) || textBuffer.length >= 10;
                        if (shouldFlush) {
                            // 首块发出前打断百度自动回复，由自有 LLM 抢占播报
                            if (first) {
                                await humanInstanceRef.current?.interrupt();
                            }
                            humanInstanceRef.current?.textStreamRender({
                                body: JSON.stringify({ first, last: false, text: textBuffer }),
                                requestId,
                                ...(first ? { onCallbackMsg: (res: any) => console.log('[textStreamRender]', res) } : {})
                            });
                            first = false;
                            textBuffer = '';
                        }
                    } catch {
                        // 跳过非 JSON 行
                    }
                }
            }

            // 流异常结束时发送残留
            if (textBuffer) {
                humanInstanceRef.current?.textStreamRender({
                    body: JSON.stringify({ first, last: false, text: textBuffer }),
                    requestId
                });
            }
            humanInstanceRef.current?.textStreamRender({
                body: JSON.stringify({ first: false, last: true, text: '' }),
                requestId
            });

            // 将助手回复存入对话历史
            if (fullReply) {
                history.push({ role: 'assistant', content: fullReply });
            } else {
                // LLM 无回复，移除刚才加入的用户消息（避免历史膨胀）
                history.pop();
            }
        } catch (err: any) {
            if (err.name !== 'AbortError') {
                console.error('[LLM] 流式请求失败:', err);
            }
            // 请求失败或被打断，移除已加入的用户消息（保持历史干净）
            const last = history[history.length - 1];
            if (last?.role === 'user' && last?.content === query) {
                history.pop();
            }
        } finally {
            isRespondingRef.current = false;
            abortControllerRef.current = null;
        }
    }, []);

    const [realTimeVideoReady, setRealTimeVideoReady] = useState(false);
    const [wsConnected, setWsConnected] = useState(false);
    // 浏览器限制导致的有声视频无法自动播放,所以这里进行静音后自动播放
    const [videoIsMuted, setVideoIsMuted] = useState(false);
    // 是否可以自动播放，检测完成状态
    const [checkOver, setCheckOver] = useState(false);
    const videoIsMutedRef = useRef(false);
    videoIsMutedRef.current = videoIsMuted;

    const handleDigitalHumanCallback = useCallback(async (data: any) => {
        const {status, content} = data;
        console.info(status, content);
        if (status === 'DH_LIB_MESSAGE') {
            switch (content.action) {
                case 'DOWN_SUBTITLE':
                    const {content: subtitleContent, completed, type} = JSON.parse(content.body);
                    if (type === 'QUERY') {
                        setSubTitle(subtitleContent); // 还在继续识别中completed：false
                        if (completed) {
                            setIsListening(false); // 识别完成，用户说完一句
                            console.info('[LLM] 完整询问内容：', subtitleContent);
                            callLlmAndDrive(subtitleContent);
                        } else {
                            setIsListening(true); // ASR 正在识别，用户正在说话
                        }
                    }
                    if (type === 'REPLY') {
                        console.info('回答：', subtitleContent);
                    }
                    break;
                case 'AUDIO_QUERY_INTERRUPT':
                    console.info('停止发送音频');
                    setIsListening(false); // ASR 被中断，停止聆听态
                    break;
                case 'EMPTY_ASR_RESULT':
                    console.info('空ASR结果');
                    break;
                case 'ASR_ERROR':
                    console.info('ASR错误:', content);
                    break;
                default:
                    break;
            }
        }
        if (status === 'DH_LIB_FULL_STATUS') {
            const {type, action} = content;
            if (type === 'rtcState') {
                if (action === 'remotevideoon') {
                    setRealTimeVideoReady(true);
                    checkPlayUnMuteFun();
                }
                if (action === 'localVideoMuted' && content.body) {
                    setVideoIsMuted(true);
                }
            }
            if (type === 'wsState') {
                if (content.readyState === ReadyState.OPEN) {
                    setWsConnected(true);
                }
                else if (content.readyState === ReadyState.CLOSED || content.readyState === ReadyState.CLOSING) {
                    setWsConnected(false);
                }
            }
        }
    }, [callLlmAndDrive]);

    const checkPlayUnMuteFun = async () => {
        const result = await checkPlayUnMute(); // 500ms左右
        setVideoIsMuted(!result);
        setCheckOver(true);
    }

    const destroyCurrentInstance = useCallback(async () => {
        if (!humanInstanceRef.current) {
            return;
        }
        try {
            await humanInstanceRef.current.destroy();
        } catch (error) {
            console.error('重连前销毁旧实例失败:', error);
        } finally {
            humanInstanceRef.current = null;
        }
    }, []);

    // 不带拾音功能的连接
    const handleConnect = useCallback(async () => {
        await destroyCurrentInstance();
        hasSentGreetingRef.current = false;
        setAudioMode('');
        const [token, config] = await Promise.all([fetchToken(), fetchConfig()]);
        humanInstanceRef.current = new RealTimeHuman({
            token,
            wrapperId: 'human-wrapper',
            connectParams: {
                ttsPer: config.ttsPer,
                figureId: config.figureId,
                resolutionHeight: 1920,
                resolutionWidth: 1080,
                inactiveDisconnectSec: 300
            },
            renderParams: {
                closeLog: true,
                autoChromaKey: false,
                fullStatus: true
            },
            onDigitalHumanCallback: handleDigitalHumanCallback
        });
        humanInstanceRef.current.createServer();
    }, [destroyCurrentInstance, handleDigitalHumanCallback]);

    // 带拾音功能的连接
    const handleConnectWithAudio = useCallback(async (mode = 'btn' as 'btn' | 'rtc') => {
        // 先销毁旧实例，避免重复创建导致 DOM ID 冲突
        await destroyCurrentInstance();
        hasSentGreetingRef.current = false;
        setAudioMode(mode);
        setPullAudioFromRtc(() => {
            return mode === 'rtc';
        });
        const [token, config] = await Promise.all([fetchToken(), fetchConfig()]);
        humanInstanceRef.current = new RealTimeHuman({
            token,
            wrapperId: 'human-wrapper',
            connectParams: {
                ttsPer: config.ttsPer,
                figureId: config.figureId,
                resolutionHeight: 1920,
                resolutionWidth: 1080,
                inactiveDisconnectSec: 3000,
                pullAudioFromRtc: mode === 'rtc',
                pickAudioMode: mode === 'btn' ? 'pressButton' : 'free',
                enableInterrupt: enableInterruptRef.current,
                // asrDdc: false,
                // asrPunc: false,
                // asrItn: false,
                // asrSensitiveWord: false,
                // asrBoostingTable: '拾音,心情,实验'
                // asrVadPauseTime: 200,
            },
            renderParams: {
                closeLog: true,
                autoChromaKey: false,
                fullStatus: true
            },
            onDigitalHumanCallback: handleDigitalHumanCallback
        });
        humanInstanceRef.current.createServer();
    }, [destroyCurrentInstance, handleDigitalHumanCallback]);

    const handleDestroy = useCallback(async () => {
        // 中止正在进行的 LLM 请求
        abortControllerRef.current?.abort();
        isRespondingRef.current = false;
        abortControllerRef.current = null;
        conversationHistoryRef.current = []; // 清空对话历史
        setSubTitle('');
        isRecordingRef.current = false;
        setIsRecording(false);
        hasSentGreetingRef.current = false;
        canTransferRef.current = false;
        setCanTransfer(false);
        setRealTimeVideoReady(false);
        setWsConnected(false);
        setCheckOver(false);
        setVideoIsMuted(false);
        setIsMuted(true);
        setIsListening(false);
        try {
            await humanInstanceRef.current?.destroy();
        } catch (error) {
            console.error('销毁数字人实例失败:', error);
        } finally {
            humanInstanceRef.current = null;
        }
    }, []);

    const handleInterrupt = useCallback(async () => {
        console.info('发送打断');
        await humanInstanceRef.current?.interrupt();
        console.info('打断生效');
    }, []);

    const handleTextRender = useCallback(() => {
        humanInstanceRef.current.textRender({
            body: '你好，这是我的开场白自我介绍，我是数字人。今天天气不错，打算去哪里玩吗？我可以给你推荐一些网红打卡点，需要的话请告诉我哦！',
            onCallbackMsg: (res: any) => console.log(res)
        });
    }, []);

    const handleStreamTextRender = useCallback(() => {
        const commandId = uuidv4();
        humanInstanceRef.current?.textStreamRender({
            body: JSON.stringify({
                first: true,
                last: false,
                text: '你好，这是我的开场白自我介绍，我是数字人'
            }),
            requestId: commandId,
            onCallbackMsg: (res: any) => console.log(res)
        });

        humanInstanceRef.current?.textStreamRender({
            body: JSON.stringify({
                first: false,
                last: false,
                text: '你好，这是我的开场白自我介绍，我是数字人'
            }),
            requestId: commandId,
            // 测试流式驱动多次填写onCallbackMsg是否兼容
            onCallbackMsg: (res: any) => console.log(res)
        });

        humanInstanceRef.current?.textStreamRender({
            body: JSON.stringify({
                first: false,
                last: false,
                text: '你好，这是我的开场白自我介绍，我是数字人'
            }),
            requestId: commandId
        });

        setTimeout(() => {
            humanInstanceRef.current?.textStreamRender({
                body: JSON.stringify({
                    first: false,
                    last: false,
                    text: '今天天气,今天天气,今天天气今天天气,今天天气,今天天气今天天气,今天天气,今天天气'
                }),
                requestId: commandId
            });

            humanInstanceRef.current?.textStreamRender({
                body: JSON.stringify({
                    first: false,
                    last: true,
                    text: '很好很好很好很好很好很好'
                }),
                requestId: commandId
            });
        }, 500);
    }, []);

    const fetchData = async (url: string): Promise<ArrayBuffer> => {
        const response = await fetch(url, {
            method: 'GET'
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        // 将响应体解析为 ArrayBuffer
        const arrayBuffer = await response.arrayBuffer();
        return arrayBuffer;
    };

    const arrayBufferToBase64 = (buffer: any) => {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    };

    const waitStop = async (time: number) => {
        return new Promise<void>(resolve => {
            setTimeout(() => resolve(), time || 100);
        });
    };

    const handleStreamAudioRender = useCallback(async () => {
        const buffer = await fetchData('https://meta-human-editor-prd.cdn.bcebos.com/open-api%2FaudioTest.pcm');
        const unitLen = 2048; // 模拟切段
        const requestId = uuidv4();
        const len = Math.ceil(buffer.byteLength / unitLen);
        for (let i = 0; i < len; ++i) {
            const arrayBuffer = buffer.slice(i * unitLen, (i + 1) * unitLen);
            const base64String = arrayBufferToBase64(arrayBuffer); // pcm转base64
            if (i % 10 === 0) {
                await waitStop(50);
            }
            humanInstanceRef.current?.audioStreamRender({
                requestId,
                body: JSON.stringify({
                    audio: base64String,
                    first: i === 0,
                    last: i === len - 1
                }),
                ...(i === 0 ? {onCallbackMsg: (res: any) => console.log(`audioRender ${JSON.stringify(res)}`)} : {})
            });
        }
    }, []);

    const handleMuteHuman = () => humanInstanceRef.current?.muteHuman();

    const handleUnMuteHuman = () => humanInstanceRef.current?.unMuteHuman();

    const handlePlayHuman = () => humanInstanceRef.current?.playHuman();

    const handlePauseHuman = () => humanInstanceRef.current?.pauseHuman();

    const changeMicrophoneState = useCallback((isMute: boolean) => {
        humanInstanceRef.current?.muteMicrophone(isMute);
        canTransferRef.current = !isMute;
        setCanTransfer(canTransferRef.current);
        setIsMuted(!canTransferRef.current);
    }, []);

    // 按键拾音开启
    const startRecord = useCallback(async () => {
        if (isRecordingRef.current) {
            return;
        }
        // 当参数enableInterrupt为true时，主动发送打断消息可更快打断播报，不发送打断消息也可打断
        // 当参数enableInterrupt为false，请注释下面打断代码。
        if (enableInterruptRef.current) {
            await humanInstanceRef.current?.interrupt();
        }
        isRecordingRef.current = true;
        setIsRecording(true);
        humanInstanceRef.current?.startRecord();
    }, []);

    // 按键拾音关闭
    const stopRecord = useCallback(() => {
        if (!isRecordingRef.current) {
            return;
        }
        isRecordingRef.current = false;
        setIsRecording(false);
        humanInstanceRef.current?.stopRecord();
    }, []);

    useEffect(() => {
        if (audioMode === 'rtc') {
            changeMicrophoneState(true); // 先静音麦克风，等开场白播完（或保护期到期）再开
        }
        // 单页模式：用户已在落地页点过播放按钮，直接发开场白
        if (realTimeVideoReady && wsConnected && checkOver && !hasSentGreetingRef.current) {
            hasSentGreetingRef.current = true;
            const commandId = uuidv4();

            // 1) 延迟 OPENING_TTS_DELAY_MS 后发送开场白命令
            const timer = setTimeout(() => {
                humanInstanceRef.current.sendMessage({
                    action: 'TEXT_RENDER',
                    body: DEFAULT_TEXT,
                    requestId: commandId
                }, ({action}) => {
                    console.log(`action====: ${action}`);
                    if (action === 'FINISHED' && audioMode === 'rtc') {
                        changeMicrophoneState(false); // 开场白播放完毕，开启麦克风
                    }
                });
            }, OPENING_TTS_DELAY_MS);

            // 2) 兜底：从发送开场白命令起算，最多 OPENING_FREE_INTERRUPT_MS 免中断
            //    到点强制解除静音，让用户可以插话打断（不论开场白是否播完）
            const forceUnmuteTimer = setTimeout(() => {
                if (audioMode === 'rtc') {
                    console.info(`[Opening] 免中断保护期 ${OPENING_FREE_INTERRUPT_MS}ms 到期，强制开启麦克风`);
                    changeMicrophoneState(false);
                }
            }, OPENING_TTS_DELAY_MS + OPENING_FREE_INTERRUPT_MS);

            return () => {
                clearTimeout(timer);
                clearTimeout(forceUnmuteTimer);
            };
        }
    }, [audioMode, realTimeVideoReady, wsConnected, checkOver, changeMicrophoneState]);

    useEffect(() => {
        // 处理原生键盘事件（避免类型不匹配React的合成事件）
        const onKeyDown = (e: KeyboardEvent) => {
            e.preventDefault(); // 阻止空格触发页面滚动
            // 如果是长按触发的重复事件，直接返回
            if (e.repeat) {
                return;
            }
            if (audioMode === 'btn' && (e.code === 'Space' || e.keyCode === 32)) {
                startRecord();
            }
        };
        const onKeyUp = (e: KeyboardEvent) => {
            if (e.repeat) {
                return;
            }
            if (audioMode === 'btn' && (e.code === 'Space' || e.keyCode === 32)) {
                stopRecord();
            }
        };
        if (audioMode === 'btn') {
            document.addEventListener('keydown', onKeyDown);
            document.addEventListener('keyup', onKeyUp);
        }
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('keyup', onKeyUp);
        };
    }, [audioMode, startRecord, stopRecord]);

    // 组件卸载时清理实例
    useEffect(() => {
        return () => {
            const inst = humanInstanceRef.current;
            humanInstanceRef.current = null;
            if (inst) {
                void inst.destroy().catch((error: unknown) => {
                    console.error('组件卸载销毁数字人实例失败:', error);
                });
            }
        };
    }, []);

    // ── 由落地页的 <script> 触发：window.startDH() 在用户点击播放按钮后调用 ──
    useEffect(() => {
        (window as any).startDH = () => {
            console.info('[startDH] 用户点击播放按钮，启动数字人...');
            void handleConnectWithAudio('rtc').catch(error => {
                console.error('[startDH] ❌ 连接失败:', error);
            });
        };
        return () => { delete (window as any).startDH; };
    }, [handleConnectWithAudio]);

    return (
        <>
            <div id="human-wrapper"></div>
            {audioMode === 'rtc' && isListening && (
                <div className="listening-hint">
                    <span className="listening-hint__dot" />
                    聆听中
                </div>
            )}
            <div className="sub-title">{subTitle}</div>
        </>
    );
};

export default Demo;
